import { inject, Service } from "@angular/core";
import { SharedDataService } from "./shared-data.service";
import { BehaviorSubject } from "rxjs/internal/BehaviorSubject";
import { BarcodeRule, ProductSelectItem } from "@app/types";
import { environment } from "@env";
import { toSignal } from "@angular/core/rxjs-interop";
import { combineLatest, filter, map } from "rxjs";
import { toFixedNumber } from "../utils";
import { MessageService } from "./message.service";

interface ScannedItem {
	product: ProductSelectItem,
	quantity: number
}

@Service()
export class BarcodeReaderService {
	readonly #messageService = inject(MessageService);
	readonly #sharedDataService = inject(SharedDataService);

	readonly #addItemEventSub$ = new BehaviorSubject<ScannedItem | undefined>(undefined);
	readonly addItemEvent$ = this.#addItemEventSub$.asObservable();

	#lastKeyEventtimestamp = 0;
	#scannedBarcode = '';
	// #isScanning = false;

	#scanData = toSignal(
		combineLatest({
			barcodeRules: this.#sharedDataService.barcodeRules$,
			allProducts: this.#sharedDataService.allProducts$,
		}).pipe(
			filter(({ barcodeRules, allProducts }) => !!barcodeRules && !!allProducts),
			map(({ barcodeRules, allProducts }) => ({
				barcodeRules,
				allProducts,
				productMap: this.#createProductsMap(allProducts, barcodeRules)
			}))
		)
	)

	async addKey(key: string, timeStamp: number): Promise<void> {
		if (timeStamp - this.#lastKeyEventtimestamp > environment.scanningDelay) {
			console.info('barcode reset');
			this.#stopScanning();
			// this.#isScanning = true;
		}
		// this.#isScanning = true;
		if (key === 'Enter') {
			console.info('key read');
			await this.#readBarcode(this.#scannedBarcode);
			this.#stopScanning();
		} else if (this.#isKeyValid(key)) {
			console.info('key: ' + String(key));
			this.#lastKeyEventtimestamp = timeStamp;
			this.#scannedBarcode = this.#scannedBarcode.concat(key);
		}
	}

	clearScannedItem(): void {
		this.#addItemEventSub$.next(undefined);
	}

	#readBarcode(barcode: string): Promise<ScannedItem | void> {
		if (!this.#checkServiceReady()) {
			return Promise.resolve();
		}

		return this.#getItemByBarcode(barcode)
			.then(item => this.#addItemEventSub$.next(item))
			.catch(error => console.info(error));
	}

	#getItemByBarcode(barcode: string): Promise<ScannedItem> {
		if (!this.#scanData()) {
			return Promise.reject(new Error('Barcode rules or products not loaded'));
		}
		const { productMap, allProducts, barcodeRules } = this.#scanData()!;
		let price: number;
		let weight: number;
		let shortBarcode: string;
		let product: ProductSelectItem | undefined;
		let that = this;
		let func = (resolve: (value: ScannedItem | PromiseLike<ScannedItem>) => void, reject: (reason?: any) => void) => {
			try {
				while (barcode.length < 13) {
					barcode = '0'.concat(barcode);
				}
				for (let barcodeRule of barcodeRules) {
					const barcode_data = barcode.match(barcodeRule.expression);
					if (barcode_data && barcode_data[1] && barcode_data[2]) {
						price = Number(barcode_data[1] + '.' + barcode_data[2]);
						shortBarcode = barcode.substr(0, 7);
						product = allProducts.find(p => p.id === productMap.get(shortBarcode));
						if (product) {
							const producePrice = product!.sale_price;
							if (barcodeRule.type === 'FP') {
								weight = price / producePrice / 6.55957;
							} else if (barcodeRule.type === 'P') {
								weight = price / producePrice;
							} else if (barcodeRule.type === 'W') {
								weight = price;
							}
							weight = toFixedNumber({ num: weight, digits: 3 });
						}
						break;
					}
					else if (barcode_data) {
						product = allProducts.find(p => p.id === productMap.get(barcode));
						weight = 1;
						break;
					}
				}
				if (product === null || product === undefined) {
					that.#cantFindProduceMessage();
					reject(new Error('Produit introuvable...'));
				}
				resolve({ product: product!, quantity: weight });
			} catch (error) {
				that.#cantFindProduceMessage();
				reject(error);
			}
		};
		return new Promise(func);
	}

	#createProductsMap(products: ProductSelectItem[], barcodeRules: BarcodeRule[]): Map<string, number> {
		const productMap = new Map<string, number>();
		for (const product of products) {
			product.barcodes = product.barcodes || [];
			for (const barcode of product.barcodes) {
				if (!barcode) {
					continue;
				}
				const shortBarcode = this.#trimBarcode(barcode, barcodeRules);
				productMap.set(shortBarcode, product.id);
			}
		}
		return productMap;
	}

	#cantFindProduceMessage() {
		this.#messageService.vibrate();
		this.#messageService.show('Produit introuvable', 'alert-error');
	}

	#stopScanning() {
		// this.#isScanning = false;
		this.#scannedBarcode = '';
		this.#lastKeyEventtimestamp = 0;
	}

	#checkServiceReady(): boolean {
		if (!this.#scanData()) {
			this.#messageService.show('Veuillez patienter, données en cours de chargement...', 'alert-error');
			return false;
		}

		return true;
	}

	#isKeyValid(key: string) {
		return ['0','1','2','3','4','5','6','7','8','9'].includes(key) ;
	}

	#trimBarcode(barcode: string, barcodeRules: BarcodeRule[]): string {
		if (barcode !== undefined && barcode !== null) {
			for (let index = 0; index < (barcodeRules || []).length; index++) {
				const barcode_data = barcode.match(barcodeRules[index].expression);
				if (barcode_data && barcode_data[1] && barcode_data[2]) {
					barcode = barcode.substr(0, 7);
				}
			}
		}
		return barcode;
	}
}
